import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Field, FormConsumer, FormProvider } from "@formily/react";
import { Modal } from "../../../components/core/basic/Modal";
import { Button } from "../../../components/core/basic/Button";
import { TextField } from "../../../components/fields/formily/TextField";
import { TextAreaField } from "../../../components/fields/formily/TextAreaField";
import { Select } from "../../../components/fields/formily/Select";
import { ColorField } from "../../../components/fields/formily/ColorField";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { useCreateTagMutation } from "../api/useCreateTagMutation";
import { useUpdateTagMutation } from "../api/useUpdateTagMutation";
import {
  TAG_SCOPE_LABELS,
  TAG_VISIBILITY_LABELS,
  type Tag,
  type TagEntityType,
  type TagScope,
  type TagVisibility,
} from "../../../zodios/apiDefinitions/api/tags";
import { useAppSelector } from "../../../redux/hooks";
import { useFormily } from "../../../hooks/useFormily";

interface TagFormModalProps {
  entityType: TagEntityType;
  initialTag?: Partial<Tag>;
  onCreated?: (tag: Tag) => void;
}

interface TagFormValues {
  name: string;
  description: string;
  color: string;
  scope: TagScope;
  visibility: TagVisibility;
}

const SCOPE_OPTION_VALUES: TagScope[] = ["ORGANIZATION", "MARKETPLACE", "AGENCY"];

export const TagFormModal = NiceModal.create(({ entityType, initialTag, onCreated }: TagFormModalProps) => {
  const { t } = useTranslation(["tags", "common"]);
  const modal = useModal();
  const createTagMutation = useCreateTagMutation();
  const updateTagMutation = useUpdateTagMutation();
  const currentMarketplace = useAppSelector((state) => state.marketPlace.marketPlace);
  const currentOrgId = useAppSelector((state) => state.user.organization_id);
  const isStaff = useAppSelector((state) => state.user.is_staff);
  const agencyId = useAppSelector((state) => state.user.account_manager.agency_id);

  const editingId = initialTag?.id;
  const isEditing = !!editingId;
  const isSystemTag = !!initialTag && initialTag.type !== undefined && initialTag.type !== "user";

  const initialValues: TagFormValues = useMemo(
    () => ({
      name: initialTag?.name ?? "",
      description: initialTag?.description ?? "",
      color: initialTag?.color ?? "#C63066",
      scope: initialTag?.scope_definition?.scope ?? "ORGANIZATION",
      visibility: initialTag?.scope_definition?.visibility ?? "ANYONE",
    }),
    [initialTag],
  );

  const visibilityOptions = useMemo(() => {
    const options: { value: TagVisibility; label: string; isDisabled?: boolean }[] = [
      { value: "USER", label: t(($) => $.tags.visibility[TAG_VISIBILITY_LABELS.USER]) },
      { value: "ANYONE", label: t(($) => $.tags.visibility[TAG_VISIBILITY_LABELS.ANYONE]) },
    ];
    if (isStaff) {
      options.push({ value: "STAFF", label: t(($) => $.tags.visibility[TAG_VISIBILITY_LABELS.STAFF]) });
    }
    return options;
  }, [isStaff, t]);

  const scopeOptions = useMemo(() => {
    return SCOPE_OPTION_VALUES.map((value) => {
      const label = t(($) => $.tags.scope[TAG_SCOPE_LABELS[value]]);
      return value === "AGENCY" && agencyId == null ? { value, label, isDisabled: true } : { value, label };
    });
  }, [agencyId, t]);

  const form = useFormily<TagFormValues>({ initialValues });

  const buildScopeDefinition = (scope: TagScope, visibility: TagVisibility) => {
    const entityId =
      scope === "ORGANIZATION" ? currentOrgId
      : scope === "MARKETPLACE" ? currentMarketplace
      : agencyId != null ? String(agencyId)
      : "";
    return { scope, visibility, entity_id: entityId };
  };

  const handleSubmit = () => {
    void form.submit((values) => {
      const scopeDefinition = buildScopeDefinition(values.scope, values.visibility);
      const tagData = {
        name: values.name.trim(),
        description: values.description.trim(),
        color: values.color,
        scope_definition: scopeDefinition,
        marketplace: currentMarketplace,
        entity_type: entityType,
        type: "user" as const,
        origin: "user",
      };

      if (editingId) {
        const updateData =
          isSystemTag ?
            {
              color: tagData.color,
              type: initialTag.type ?? "user",
              entity_type: initialTag.entity_type ?? entityType,
            }
          : {
              name: tagData.name,
              description: tagData.description,
              color: tagData.color,
              scope_definition: tagData.scope_definition,
              marketplace: tagData.marketplace,
              type: initialTag.type ?? "user",
              entity_type: initialTag.entity_type ?? entityType,
            };
        updateTagMutation.mutate(
          {
            id: editingId,
            data: updateData,
          },
          {
            onSuccess: () => {
              modal.remove();
            },
          },
        );
      } else {
        createTagMutation.mutate(tagData, {
          onSuccess: (createdTag) => {
            onCreated?.(createdTag);
            modal.remove();
          },
        });
      }
    });
  };

  const isPending = createTagMutation.isPending || updateTagMutation.isPending;

  return (
    <Modal
      isOpen={modal.visible}
      onOpenChange={(open) => {
        if (!open) modal.remove();
      }}
      isDismissable
      title={
        isSystemTag ? t(($) => $.tags.form["Edit System Tag"])
        : isEditing ?
          t(($) => $.tags.form["Edit Tag"])
        : t(($) => $.tags.form["Create Tag"])
      }
      css={{
        width: "500px",
        maxWidth: "90vw",
        padding: "large",
      }}
    >
      <FormProvider form={form}>
        <VStack gap="medium" alignItems="stretch" paddingX="large">
          <Field
            name="name"
            title={t(($) => $.tags.form["Name"])}
            disabled={isSystemTag}
            validator={(value) => (!value?.trim() ? t(($) => $.tags.form["Name is required"]) : true)}
            component={[TextField, { autoFocus: !isSystemTag }]}
          />

          <Field
            name="description"
            title={t(($) => $.tags.form["Description"])}
            disabled={isSystemTag}
            component={[
              TextAreaField,
              { rows: 3, placeholder: t(($) => $.tags.form["Optional description for this tag"]) },
            ]}
          />

          <Field
            name="color"
            title={t(($) => $.tags.form["Color"])}
            validator={(value) =>
              value && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value) ?
                t(($) => $.tags.form["Invalid hex color format"])
              : true
            }
            component={[ColorField]}
          />

          <Field
            name="scope"
            title={t(($) => $.tags.form["Scope"])}
            disabled={isSystemTag}
            dataSource={scopeOptions}
            description={t(($) => $.tags.form["Which entity this tag belongs to"])}
            component={[Select]}
          />

          <Field
            name="visibility"
            title={t(($) => $.tags.form["Visibility"])}
            disabled={isSystemTag}
            dataSource={visibilityOptions}
            description={t(($) => $.tags.form["Who can see and use this tag"])}
            component={[Select]}
          />
        </VStack>

        <HStack gap="medium" justifyContent="center" paddingTop="large">
          <Button variant="secondary" onPress={() => modal.remove()} isDisabled={isPending}>
            {t(($) => $.common.actions.Cancel)}
          </Button>
          <FormConsumer>
            {(form) => (
              <Button variant="primary" onPress={handleSubmit} isPending={isPending} isDisabled={form.invalid}>
                {isEditing ? t(($) => $.tags.form["Save Changes"]) : t(($) => $.tags.form["Create Tag"])}
              </Button>
            )}
          </FormConsumer>
        </HStack>
      </FormProvider>
    </Modal>
  );
});
